import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Play, Bookmark, BookmarkCheck, PlayCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useAudio } from '../context/AudioContext';
import { useLanguage } from '../context/LanguageContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import AdInterstitial from '../components/AdInterstitial';
import { useAdFrequency } from '../hooks/useAdFrequency';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SurahReader = () => {
  const { chapterId } = useParams();
  const navigate = useNavigate();
  const [chapter, setChapter] = useState(null);
  const [verses, setVerses] = useState([]);
  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [translations, setTranslations] = useState([]);
  const [selectedTranslation, setSelectedTranslation] = useState('en.sahih');
  const { playVerse, playFullSurah, currentVerse } = useAudio();
  const { t } = useLanguage();
  
  // Interstitial ad management
  const { shouldShowAd, markAdShown } = useAdFrequency(3); // Show every 3 surah views
  const [showInterstitial, setShowInterstitial] = useState(false);

  useEffect(() => {
    if (shouldShowAd) {
      setShowInterstitial(true);
      markAdShown();
    }
  }, [shouldShowAd, markAdShown]);

  // Auto-scroll to currently playing verse
  useEffect(() => {
    if (currentVerse && currentVerse.chapterId === parseInt(chapterId)) {
      const verseElement = document.querySelector(`[data-testid="ayah-${currentVerse.verseNumber}"]`);
      if (verseElement) {
        verseElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [currentVerse, chapterId]);

  useEffect(() => {
    fetchTranslations();
  }, []);

  useEffect(() => {
    fetchChapterData();
    fetchBookmarks();
  }, [chapterId, selectedTranslation]);

  const fetchTranslations = async () => {
    try {
      const response = await axios.get(`${API}/translations`);
      setTranslations(response.data);
    } catch (error) {
      console.error('Error fetching translations:', error);
    }
  };

  const fetchChapterData = async () => {
    try {
      setLoading(true);
      const [chapterRes, versesRes] = await Promise.all([
        axios.get(`${API}/chapters/${chapterId}`),
        axios.get(`${API}/verses/${chapterId}`, { 
          params: { 
            per_page: 50,
            translation: selectedTranslation 
          } 
        })
      ]);
      setChapter(chapterRes.data);
      setVerses(versesRes.data);
    } catch (error) {
      console.error('Error fetching chapter data:', error);
      toast.error('Failed to load surah');
    } finally {
      setLoading(false);
    }
  };

  const fetchBookmarks = async () => {
    try {
      const response = await axios.get(`${API}/bookmarks`);
      setBookmarks(response.data);
    } catch (error) {
      console.error('Error fetching bookmarks:', error);
    }
  };

  const isBookmarked = (verseKey) => {
    return bookmarks.some((b) => b.verse_key === verseKey);
  };

  const toggleBookmark = async (verse) => {
    try {
      if (isBookmarked(verse.verse_key)) {
        await axios.delete(`${API}/bookmarks/${verse.verse_key}`);
        toast.success(t('bookmarkRemoved'));
      } else {
        await axios.post(`${API}/bookmarks`, {
          verse_key: verse.verse_key,
          chapter_id: parseInt(chapterId),
          verse_number: verse.verse_number
        });
        toast.success(t('bookmarkAdded'));
      }
      fetchBookmarks();
    } catch (error) {
      console.error('Error toggling bookmark:', error);
      toast.error(t('failedToDeleteBookmark'));
    }
  };

  const handlePlayVerse = (verse) => {
    playVerse({
      chapterId: parseInt(chapterId),
      verseNumber: verse.verse_number,
      verseKey: verse.verse_key
    });
  };

  const handlePlayFullSurah = () => {
    if (verses.length === 0) return;
    playFullSurah(parseInt(chapterId), verses);
    toast.success(t('playingFullSurah'));
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-[#6B726B]">Loading...</div>;
  }

  return (
    <div className="min-h-screen pb-32">
      {/* Interstitial Ad */}
      <AdInterstitial 
        show={showInterstitial} 
        onClose={() => setShowInterstitial(false)}
        adSlot="6132496444"
        autoCloseDelay={5}
      />

      {/* Header */}
      <div className="bg-white border-b border-[#E8EAE8]">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-[#3A5A40] hover:text-[#2B4330] mb-6"
            data-testid="back-button"
          >
            <ArrowLeft size={20} strokeWidth={1.5} />
            <span className="font-medium">{t('backToSurahs')}</span>
          </button>

          {chapter && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              <div className="flex justify-between items-start mb-6">
                <div>
                  <div className="text-sm text-[#A3B18A] font-medium mb-2" style={{ letterSpacing: '0.2em' }}>
                    {t(chapter.revelation_place.toLowerCase())} · {chapter.verses_count} {t('ayahs').toUpperCase()}
                  </div>
                  <h1 className="text-4xl sm:text-5xl font-light tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="surah-title">
                    {chapter.name_simple}
                  </h1>
                </div>
                <div className="text-4xl sm:text-5xl arabic-text" style={{ fontFamily: 'Amiri, serif' }}>
                  {chapter.name_arabic}
                </div>
              </div>

              {/* Translation Selector and Play Full Surah Button */}
              <div className="flex flex-wrap gap-4 items-center">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[#6B726B]">{t('translation')}:</span>
                  <Select value={selectedTranslation} onValueChange={setSelectedTranslation}>
                    <SelectTrigger className="w-[200px] border-[#E8EAE8] bg-white" data-testid="translation-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-[#E8EAE8]">
                      {translations.map((trans) => (
                        <SelectItem key={trans.id} value={trans.id} data-testid={`translation-option-${trans.id}`}>
                          {trans.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <button
                  onClick={handlePlayFullSurah}
                  className="flex items-center gap-2 px-6 py-2 bg-[#3A5A40] hover:bg-[#2B4330] text-white rounded-full transition-colors"
                  data-testid="play-full-surah-btn"
                >
                  <PlayCircle size={20} strokeWidth={1.5} />
                  <span className="font-medium">{t('playFullSurah')}</span>
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Verses */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="space-y-8">
          {verses.map((verse, index) => {
            const isPlaying = currentVerse?.verseKey === verse.verse_key;
            const bookmarked = isBookmarked(verse.verse_key);

            return (
              <div key={verse.id}>
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 * index }}
                  className={`ayah-container border-b border-[#E8EAE8]/50 pb-8 ${isPlaying ? 'playing-ayah' : ''}`}
                  data-testid={`ayah-${verse.verse_number}`}
                >
                {/* Verse Number */}
                <div className="flex items-center justify-between mb-4">
                  <div className="w-8 h-8 rounded-full bg-[#F4F5F4] flex items-center justify-center text-[#D4A373] text-sm font-medium">
                    {verse.verse_number}
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => handlePlayVerse(verse)}
                      className="bookmark-btn hover:text-[#3A5A40]"
                      data-testid={`play-ayah-${verse.verse_number}`}
                    >
                      <Play size={18} strokeWidth={1.5} fill={isPlaying ? 'currentColor' : 'none'} />
                    </button>
                    <button
                      onClick={() => toggleBookmark(verse)}
                      className={`bookmark-btn hover:text-[#D4A373] ${bookmarked ? 'active text-[#D4A373]' : ''}`}
                      data-testid={`bookmark-ayah-${verse.verse_number}`}
                    >
                      {bookmarked ? <BookmarkCheck size={18} strokeWidth={1.5} fill="currentColor" /> : <Bookmark size={18} strokeWidth={1.5} />}
                    </button>
                  </div>
                </div>

                {/* Arabic Text */}
                <div className="text-3xl sm:text-4xl lg:text-5xl mb-6 arabic-text" style={{ fontFamily: 'Amiri, serif', lineHeight: 2.5 }}>
                  {verse.text_uthmani}
                </div>

                {/* Translation */}
                {verse.translations && verse.translations.length > 0 && (
                  <div className="text-base sm:text-lg leading-relaxed text-[#1A1C1A]" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                    {verse.translations[0].text}
                  </div>
                )}
              </motion.div>
            </div>
          );
        })}
        </div>
      </div>
    </div>
  );
};

export default SurahReader;